import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface Domain {
  id: number;
  domain: string;
  verification_status: string;
  dkim_enabled: boolean;
  mail_from_domain: string;
  default_from_email: string;
  bounce_rate: number;
  complaint_rate: number;
  created_at: string;
  dnsRecords?: DNSRecord[];
  allRecordsVerified?: boolean;
}

interface DNSRecord {
  record_type: string;
  record_name: string;
  record_value: string;
  ttl: number;
  priority?: number;
  is_verified: boolean;
  instruction?: string;
  status?: string;
}

export const DomainManager: React.FC = () => {
  const navigate = useNavigate();
  const [domains, setDomains] = useState<Domain[]>([]);
  const [newDomain, setNewDomain] = useState('');
  const [selectedDomain, setSelectedDomain] = useState<Domain | null>(null);
  const [dnsRecords, setDnsRecords] = useState<DNSRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'add' | 'manage' | 'verify'>('add');
  const [verificationStatus, setVerificationStatus] = useState<Record<number, string>>({});
  const [copiedRecord, setCopiedRecord] = useState<string | null>(null);

  useEffect(() => {
    fetchDomains();
  }, []);

  const fetchDomains = async () => {
    try {
      const response = await fetch('http://localhost:8787/api/domains?userId=1');
      const data = await response.json();
      if (data.success) {
        setDomains(data.domains);
      }
    } catch (error) {
      console.error('Failed to fetch domains:', error);
    }
  };

  const addDomain = async () => {
    if (!newDomain) return;

    setLoading(true);
    try {
      const response = await fetch('http://localhost:8787/api/domains', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          domain: newDomain.toLowerCase().trim(),
          enableMailFrom: true,
          userId: 1
        })
      });

      const data = await response.json();

      if (data.success) {
        setDnsRecords(data.dnsRecords);
        await fetchDomains();
        setActiveTab('verify');
        setNewDomain('');

        // Find the newly added domain and select it
        const newDomainObj = domains.find(d => d.domain === newDomain.toLowerCase().trim());
        if (newDomainObj) {
          setSelectedDomain(newDomainObj);
        }
      } else {
        alert(data.error || 'Failed to add domain');
      }
    } catch (error) {
      console.error('Error adding domain:', error);
      alert('Failed to add domain');
    } finally {
      setLoading(false);
    }
  };

  const fetchDNSRecords = async (domainId: number) => {
    try {
      const response = await fetch(`http://localhost:8787/api/domains/${domainId}/dns`);
      const data = await response.json();
      if (data.success) {
        setDnsRecords(data.records);
      }
    } catch (error) {
      console.error('Failed to fetch DNS records:', error);
    }
  };

  const verifyDomain = async (domainId: number) => {
    setVerificationStatus({ ...verificationStatus, [domainId]: 'checking' });

    try {
      const response = await fetch(`http://localhost:8787/api/domains/${domainId}/verify`, {
        method: 'POST'
      });

      const data = await response.json();

      if (data.success) {
        setVerificationStatus({ ...verificationStatus, [domainId]: 'verified' });
        await fetchDomains();
      } else {
        setVerificationStatus({ ...verificationStatus, [domainId]: 'pending' });
      }

      alert(data.message);
    } catch (error) {
      setVerificationStatus({ ...verificationStatus, [domainId]: 'error' });
      console.error('Failed to verify domain:', error);
    }
  };

  const copyToClipboard = (text: string, recordId: string) => {
    navigator.clipboard.writeText(text);
    setCopiedRecord(recordId);
    setTimeout(() => setCopiedRecord(null), 2000);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'verified': return 'text-green-500';
      case 'pending': return 'text-yellow-500';
      case 'failed': return 'text-red-500';
      default: return 'text-gray-500';
    }
  };

  const getRecordTypeColor = (type: string) => {
    switch (type) {
      case 'TXT': return 'bg-blue-500';
      case 'CNAME': return 'bg-purple-500';
      case 'MX': return 'bg-green-500';
      case 'SPF': return 'bg-orange-500';
      case 'DKIM': return 'bg-pink-500';
      case 'DMARC': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">🌐 Domain Management Center</h1>
          <p className="text-gray-400">Manage your email sending domains for AWS SES</p>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('add')}
            className={`px-4 py-2 rounded-lg font-bold transition ${
              activeTab === 'add' ? 'bg-blue-600' : 'bg-gray-800 hover:bg-gray-700'
            }`}
          >
            ➕ Add Domain
          </button>
          <button
            onClick={() => setActiveTab('manage')}
            className={`px-4 py-2 rounded-lg font-bold transition ${
              activeTab === 'manage' ? 'bg-blue-600' : 'bg-gray-800 hover:bg-gray-700'
            }`}
          >
            📊 Manage Domains
          </button>
          <button
            onClick={() => setActiveTab('verify')}
            className={`px-4 py-2 rounded-lg font-bold transition ${
              activeTab === 'verify' ? 'bg-blue-600' : 'bg-gray-800 hover:bg-gray-700'
            }`}
          >
            ✅ Verify DNS
          </button>
          <button
            onClick={() => navigate('/send-email')}
            className="ml-auto px-4 py-2 rounded-lg font-bold bg-green-600 hover:bg-green-500 transition"
          >
            📧 Send Email →
          </button>
        </div>

        {/* Add Domain Tab */}
        {activeTab === 'add' && (
          <div className="bg-gray-800 rounded-lg p-6">
            <h2 className="text-xl font-bold mb-4">Add New Domain</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Domain Name</label>
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={newDomain}
                    onChange={(e) => setNewDomain(e.target.value)}
                    placeholder="biig.dev"
                    className="flex-1 px-4 py-2 bg-gray-900 rounded border border-gray-700 text-white"
                  />
                  <button
                    onClick={addDomain}
                    disabled={!newDomain || loading}
                    className="px-6 py-2 bg-blue-600 rounded font-bold hover:bg-blue-700 disabled:opacity-50"
                  >
                    {loading ? 'Adding...' : 'Add Domain'}
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  Enter your domain name (e.g., biig.dev, example.com)
                </p>
              </div>

              <div className="bg-blue-900/20 border border-blue-600 rounded-lg p-4">
                <h3 className="font-bold text-blue-400 mb-2">📝 What happens when you add a domain:</h3>
                <ol className="list-decimal list-inside space-y-1 text-sm">
                  <li>We generate verification tokens and DKIM keys</li>
                  <li>We provide DNS records for you to add</li>
                  <li>You add these records to your DNS provider</li>
                  <li>We verify the records and activate the domain</li>
                  <li>You can start sending emails from this domain</li>
                </ol>
              </div>

              <div className="bg-yellow-900/20 border border-yellow-600 rounded-lg p-4">
                <h3 className="font-bold text-yellow-400 mb-2">⚡ Quick Setup for biig.dev:</h3>
                <div className="space-y-2 text-sm">
                  <p>Since you mentioned biig.dev, here's what we'll set up:</p>
                  <ul className="space-y-1">
                    <li>✉️ Default FROM: <code>noreply@biig.dev</code></li>
                    <li>📬 MAIL FROM: <code>mail.biig.dev</code></li>
                    <li>🔐 DKIM signing for authentication</li>
                    <li>📋 SPF and DMARC policies</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Manage Domains Tab */}
        {activeTab === 'manage' && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold mb-4">Your Domains</h2>

            {domains.length === 0 ? (
              <div className="bg-gray-800 rounded-lg p-8 text-center">
                <p className="text-gray-400 mb-4">No domains added yet</p>
                <button
                  onClick={() => setActiveTab('add')}
                  className="px-4 py-2 bg-blue-600 rounded font-bold hover:bg-blue-700"
                >
                  Add Your First Domain
                </button>
              </div>
            ) : (
              domains.map((domain) => (
                <div key={domain.id} className="bg-gray-800 rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-lg font-bold">{domain.domain}</h3>
                      <div className="flex gap-4 mt-2 text-sm">
                        <span className={getStatusColor(domain.verification_status)}>
                          Status: {domain.verification_status}
                        </span>
                        <span className="text-gray-400">
                          DKIM: {domain.dkim_enabled ? '✅' : '❌'}
                        </span>
                        <span className="text-gray-400">
                          Bounce Rate: {domain.bounce_rate}%
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Default FROM: {domain.default_from_email}
                      </p>
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setSelectedDomain(domain);
                          fetchDNSRecords(domain.id);
                          setActiveTab('verify');
                        }}
                        className="px-3 py-1 bg-blue-600 rounded text-sm font-bold hover:bg-blue-700"
                      >
                        View DNS
                      </button>
                      {domain.verification_status !== 'verified' && (
                        <button
                          onClick={() => verifyDomain(domain.id)}
                          disabled={verificationStatus[domain.id] === 'checking'}
                          className="px-3 py-1 bg-green-600 rounded text-sm font-bold hover:bg-green-700 disabled:opacity-50"
                        >
                          {verificationStatus[domain.id] === 'checking' ? 'Checking...' : 'Verify'}
                        </button>
                      )}
                    </div>
                  </div>

                  {domain.allRecordsVerified && (
                    <div className="mt-3 p-2 bg-green-900/20 border border-green-600 rounded">
                      <p className="text-sm text-green-400">
                        ✅ All DNS records verified - Ready to send emails!
                      </p>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {/* Verify DNS Tab */}
        {activeTab === 'verify' && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold mb-4">DNS Records Setup</h2>

            {selectedDomain ? (
              <>
                <div className="bg-gray-800 rounded-lg p-4 mb-4">
                  <h3 className="text-lg font-bold">{selectedDomain.domain}</h3>
                  <p className="text-sm text-gray-400">
                    Add these DNS records to your domain provider
                  </p>
                </div>

                {dnsRecords.map((record, index) => (
                  <div key={index} className="bg-gray-800 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <span className={`px-2 py-1 rounded text-xs font-bold text-white ${getRecordTypeColor(record.record_type)}`}>
                          {record.record_type}
                        </span>
                        <span className="text-sm font-medium">
                          {record.status || (record.is_verified ? '✅ Verified' : '⏳ Pending')}
                        </span>
                      </div>
                      <button
                        onClick={() => copyToClipboard(record.record_value, `${record.record_type}-${index}`)}
                        className="px-3 py-1 bg-gray-700 rounded text-sm hover:bg-gray-600"
                      >
                        {copiedRecord === `${record.record_type}-${index}` ? '✅ Copied!' : '📋 Copy Value'}
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <label className="text-gray-400">Name/Host:</label>
                        <code className="block bg-black/50 p-2 rounded mt-1 break-all">
                          {record.record_name}
                        </code>
                      </div>
                      <div>
                        <label className="text-gray-400">Value/Points to:</label>
                        <code className="block bg-black/50 p-2 rounded mt-1 break-all">
                          {record.record_value}
                        </code>
                      </div>
                    </div>

                    {record.priority && (
                      <div className="mt-3">
                        <label className="text-gray-400 text-sm">Priority:</label>
                        <code className="ml-2">{record.priority}</code>
                      </div>
                    )}

                    <div className="mt-3">
                      <label className="text-gray-400 text-sm">TTL:</label>
                      <code className="ml-2">{record.ttl} seconds</code>
                    </div>
                  </div>
                ))}

                <div className="bg-green-900/20 border border-green-600 rounded-lg p-4">
                  <h3 className="font-bold text-green-400 mb-2">📋 DNS Provider Instructions:</h3>
                  <ol className="list-decimal list-inside space-y-1 text-sm">
                    <li>Log into your DNS provider (Cloudflare, GoDaddy, Namecheap, etc.)</li>
                    <li>Navigate to DNS settings for {selectedDomain.domain}</li>
                    <li>Add each record exactly as shown above</li>
                    <li>Wait 10-15 minutes for propagation</li>
                    <li>Click "Verify Domain" to check the records</li>
                  </ol>
                </div>
              </>
            ) : (
              <div className="bg-gray-800 rounded-lg p-8 text-center">
                <p className="text-gray-400 mb-4">Select a domain to view DNS records</p>
                <button
                  onClick={() => setActiveTab('manage')}
                  className="px-4 py-2 bg-blue-600 rounded font-bold hover:bg-blue-700"
                >
                  View Domains
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};